import React, { useState, useRef, useEffect, useMemo } from "react";
import "./CaseCard.scss";
import { useNavigate } from "react-router-dom";
import { IoIosArrowDown } from "react-icons/io"; // Removed unused IoIosArrowUp
import gsap from "gsap";
import { WrittenFormat } from "../WrittenFormat";
import { highCourtMap } from "../../constants/CourtNames";
import { truncatePartyNames } from "../TruncateParties";
import { getLabelByCourtRef } from "../../services/api/LabelApi";
import { useBubbleSettings } from "../../context/BubbleSettingsContext";
import { MdDelete, MdEdit, MdAdd } from "react-icons/md";
import DeleteConfirmationModal from "../DeleteConfirmationModal/DeleteConfirmationModal";
import { useToast } from "../../context/ToastContext";
import {
  AddOrUpdateReAppealInCases,
  DeleteDistrictCourtManualCaseByCourtRef,
  DeleteHighCourtManualCaseByCourtRef,
  DeleteOtherCourtManualCaseByCourtRef,
  DeleteSupremeCourtManualCaseByCourtRef,
  GetReAppealedCasesByCompany,
} from "../../services/api/CasesApi";
import { useAuth } from "../../context/AuthContext";
import { updateUnseenNotification } from "../../services/api/NotificationsApi";
import { useCaseSelection } from "../../context/CaseSelectionContext";
import { useCaseLimit } from "../../context/CaseLimitContext";

// Helper component to highlight text
const HighlightText = ({ text, highlight }) => {
  if (!highlight || !text) return <span>{text}</span>;

  // Escape special regex characters (like brackets, plus signs, etc.)
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  try {
    const regex = new RegExp(`(${escapeRegExp(highlight)})`, "gi");
    const parts = text.toString().split(regex);

    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === highlight.toLowerCase() ? (
            <span key={i} className="search-highlight">
              {part}
            </span>
          ) : (
            part
          ),
        )}
      </span>
    );
  } catch (e) {
    console.error("Highlight error:", e);
    return <span>{text}</span>;
  }
};

// Re-Appeal Modal Component
const ReAppealModal = ({
  isOpen,
  onClose,
  onSubmit,
  decisionDate,
  existingReAppealData,
}) => {
  const [appealPeriodDays, setAppealPeriodDays] = useState("");
  const [appealDeadlineDate, setAppealDeadlineDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Constants for date dropdowns
  const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const currentYear = new Date().getFullYear();
  const YEARS = Array.from({ length: 20 }, (_, i) => currentYear + i);

  useEffect(() => {
    if (existingReAppealData && isOpen) {
      // Handle null appealPeriodDays
      setAppealPeriodDays(
        existingReAppealData.appealPeriodDays != null
          ? existingReAppealData.appealPeriodDays.toString()
          : "",
      );
      setAppealDeadlineDate(existingReAppealData.appealDeadlineDate || "");
    } else if (isOpen && !existingReAppealData) {
      setAppealPeriodDays("");
      setAppealDeadlineDate("");
    }
  }, [existingReAppealData, isOpen]);

  // Helper function to parse date in format "20th August 2025"
  const parseCustomDate = (dateString) => {
    if (!dateString) return null;

    try {
      // Remove ordinal suffixes (st, nd, rd, th)
      const cleanedDate = dateString.replace(/(\d+)(st|nd|rd|th)/, "$1");

      // Parse the cleaned date
      const parsedDate = new Date(cleanedDate);

      // Check if date is valid
      if (isNaN(parsedDate.getTime())) {
        console.warn("Invalid date format:", dateString);
        return null;
      }

      return parsedDate;
    } catch (error) {
      console.error("Error parsing date:", dateString, error);
      return null;
    }
  };

  const ordinalSuffix = (dayNum) => {
    const j = dayNum % 10;
    const k = dayNum % 100;
    if (j === 1 && k !== 11) return dayNum + "st";
    if (j === 2 && k !== 12) return dayNum + "nd";
    if (j === 3 && k !== 13) return dayNum + "rd";
    return dayNum + "th";
  };

  useEffect(() => {
    // Only calculate new date if user is changing days and not using existing data
    if (appealPeriodDays && parseInt(appealPeriodDays) > 0) {
      let baseDate;

      // Use decision date if available, otherwise use current date
      if (decisionDate) {
        baseDate = parseCustomDate(decisionDate);
        if (!baseDate) {
          baseDate = new Date(); // fallback to current date
        }
      } else {
        baseDate = new Date(); // Use current date as fallback
      }

      const deadline = new Date(baseDate);
      deadline.setDate(baseDate.getDate() + parseInt(appealPeriodDays));

      // Format to match your required format "29th August 2026"
      const day = deadline.getDate();

      const formattedDate = `${ordinalSuffix(
        day,
      )} ${deadline.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      })}`;

      setAppealDeadlineDate(formattedDate);
    }
  }, [appealPeriodDays, decisionDate, existingReAppealData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Allow submission even if appealPeriodDays is empty (for cases where it might be null)
    if (!appealPeriodDays && !appealDeadlineDate) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        appealPeriodDays: appealPeriodDays ? parseInt(appealPeriodDays) : null,
        appealDeadlineDate,
      });
      setAppealPeriodDays("");
      setAppealDeadlineDate("");
      onClose();
    } catch (error) {
      console.error("Error submitting re-appeal data:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleClose = () => {
    setAppealPeriodDays("");
    setAppealDeadlineDate("");
    onClose();
  };

  // --- New Logic for Date Dropdowns ---

  // Parse the deadline date string into parts for the dropdowns
  let parsedDate = { day: "", month: "", year: "" };
  if (appealDeadlineDate) {
    const dateObj = parseCustomDate(appealDeadlineDate);
    if (dateObj && !isNaN(dateObj.getTime())) {
      parsedDate.day = dateObj.getDate();
      parsedDate.month = MONTHS[dateObj.getMonth()];
      parsedDate.year = dateObj.getFullYear();
    }
  }

  // Handle changes from the date dropdowns and update the state
  const handleDateChange = (part, value) => {
    const newDateParts = { ...parsedDate };

    if (part === "day" || part === "year") {
      newDateParts[part] = parseInt(value, 10);
    } else {
      newDateParts[part] = value;
    }

    if (newDateParts.day && newDateParts.month && newDateParts.year) {
      const dayWithSuffix = ordinalSuffix(newDateParts.day);
      const newFormattedDate = `${dayWithSuffix} ${newDateParts.month} ${newDateParts.year}`;
      setAppealDeadlineDate(newFormattedDate);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="reappeal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {existingReAppealData
              ? "Update Re-Appeal Data"
              : "Add Re-Appeal Data"}
          </h3>
          <button type="button" className="close-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="appealPeriodDays">Appeal Period (Days):</label>
              <input
                type="number"
                id="appealPeriodDays"
                name="appealPeriodDays"
                value={appealPeriodDays}
                onChange={(e) => setAppealPeriodDays(e.target.value)}
                min="1"
                max="365"
                required
                placeholder="Enter number of days"
                autoFocus
              />
              {decisionDate && (
                <small
                  style={{
                    color: "#6b7280",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  Decision Date: {decisionDate}
                </small>
              )}
            </div>

            {appealDeadlineDate && (
              <div className="form-group">
                <label>Appeal Deadline Date:</label>
                <div className="date-select-container">
                  {/* Day Dropdown */}
                  <select
                    name="day"
                    className="date-select"
                    value={parsedDate.day}
                    onChange={(e) => handleDateChange("day", e.target.value)}
                  >
                    <option value="" disabled>
                      Day
                    </option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>

                  {/* Month Dropdown */}
                  <select
                    name="month"
                    className="date-select"
                    value={parsedDate.month}
                    onChange={(e) => handleDateChange("month", e.target.value)}
                  >
                    <option value="" disabled>
                      Month
                    </option>
                    {MONTHS.map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </select>

                  {/* Year Dropdown */}
                  <select
                    name="year"
                    className="date-select"
                    value={parsedDate.year}
                    onChange={(e) => handleDateChange("year", e.target.value)}
                  >
                    <option value="" disabled>
                      Year
                    </option>
                    {YEARS.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="calculated-date-display">
                  Selected Date: <strong>{appealDeadlineDate}</strong>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                onClick={handleClose}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  (!appealPeriodDays && !appealDeadlineDate) || isSubmitting
                }
                className="submit-btn"
              >
                {isSubmitting
                  ? existingReAppealData
                    ? "Updating..."
                    : "Adding..."
                  : existingReAppealData
                    ? "Update Re-Appeal Data"
                    : "Add Re-Appeal Data"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default function CaseCard({
  caseData,
  isSelected,
  onSelectToggle,
  selectedCourtType,
  selectedCaseType,
  onCaseDeleted,
  allReAppealData,
  showCheckboxes,
  serialNumber,
  searchTerm,
}) {
  const navigate = useNavigate();
  const isMobile = window.innerWidth <= 768;
  const [expanded, setExpanded] = useState(false);
  const [casecardlabels, setCaseCardLabels] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReAppealModal, setShowReAppealModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [reAppealData, setReAppealData] = useState(null);
  const { showToast } = useToast();
  const detailsRef = useRef(null);
  const { role } = useAuth();

  const { bubbleSettings } = useBubbleSettings();
  const { fetchAndUpdateBubbleSettings } = useBubbleSettings();

  const { triggerCaseRefresh } = useCaseSelection();
  const { refreshCaseLimitData } = useCaseLimit();

  // Find re-appeal data for this specific case from the passed allReAppealData
  useEffect(() => {
    const currentCourtRef = caseData?.viewDetails?.courtRef;

    if (currentCourtRef && allReAppealData) {
      // Since allReAppealData doesn't have courtRef, we need a different approach
      // If allReAppealData is a single object for this case, use it directly
      if (
        typeof allReAppealData === "object" &&
        !Array.isArray(allReAppealData)
      ) {
        setReAppealData(allReAppealData);
      }
      // If it's an array, find the matching one (if courtRef gets added later)
      else if (Array.isArray(allReAppealData)) {
        const matchingReAppealData = allReAppealData.find(
          (item) => item.courtRef === currentCourtRef,
        );
        setReAppealData(matchingReAppealData || null);
      } else {
        setReAppealData(null);
      }
    } else {
      setReAppealData(null);
    }
  }, [caseData?.viewDetails?.courtRef, allReAppealData]);

  useEffect(() => {
    const el = detailsRef.current;
    if (!el) return;

    gsap.killTweensOf(el);
    el.style.overflow = "hidden";

    if (expanded) {
      el.style.display = "block";
      const height = el.scrollHeight;

      gsap.fromTo(
        el,
        { height: 0, opacity: 0 },
        {
          height,
          opacity: 1,
          duration: 0.7,
          ease: "power2.out",
          onComplete: () => {
            el.style.height = "auto";
          },
        },
      );
    } else {
      const height = el.scrollHeight;

      gsap.fromTo(
        el,
        { height, opacity: 1 },
        {
          height: 0,
          opacity: 0,
          duration: 0.7,
          ease: "power2.in",
          onComplete: () => {
            el.style.display = "none";
          },
        },
      );
    }
  }, [expanded]);

  const [fetchedRefs, setFetchedRefs] = useState(new Set());

  const courtRef = caseData?.viewDetails?.courtRef;

  const parseDate = (dateString) => {
    if (!dateString) return null;

    try {
      // Handle different date formats that might be in your system
      // Remove ordinal suffixes (st, nd, rd, th) if present
      const cleanedDate = dateString.replace(/(\d+)(st|nd|rd|th)/, "$1");

      // Try parsing the cleaned date
      const parsedDate = new Date(cleanedDate);

      // Check if date is valid
      if (isNaN(parsedDate.getTime())) {
        console.warn("Invalid date format:", dateString);
        return null;
      }

      return parsedDate;
    } catch (error) {
      console.error("Error parsing date:", dateString, error);
      return null;
    }
  };

  useEffect(() => {
    if (!courtRef || fetchedRefs.has(courtRef)) return;

    getLabelByCourtRef(courtRef)
      .then((res) => {
        setCaseCardLabels(res.data);
        setFetchedRefs((prev) => new Set(prev).add(courtRef));
      })
      .catch((err) => {
        console.error("Error fetching casecardlabels:", err);
        setCaseCardLabels([]);
      });
  }, [courtRef, fetchedRefs]);

  const {
    parties,
    caseDetails = {},
    caseStatus = {},
    // Removed unused destructured variables: caseTypeAndNumber, advocates, viewDetails
  } = caseData;

  // Check if case status is disposed (not pending)
  // SC uses caseStatus["Status"]; HC/DC use caseStatus["Case Status"]
  const caseStatusValue = caseStatus["Case Status"] || caseStatus["Status"] || "";
  const isDisposed = caseStatusValue.toLowerCase().includes("disposed");

  const isAppealable = (() => {
    if (!isDisposed) return false;

    const decisionDateStr = caseData?.caseStatus?.["Decision Date"];
    if (!decisionDateStr) return false;

    const decisionDate = parseDate(decisionDateStr);
    if (!decisionDate || isNaN(decisionDate.getTime())) return false;

    const now = new Date();
    const diffMs = now.getTime() - decisionDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Case is appealable if decision was made within the last 60 days (0-59 days)
    return diffDays >= 0 && diffDays < 60;
  })();

  // const handlecasedetailByRef = () => {
  //   const courtref = caseData?.viewDetails?.courtRef;
  //   if (courtref) {
  //     navigate(`/home/cases/casedetails/${courtref}`, {
  //       state: { selectedCourtType, selectedCaseType },
  //     });
  //   }
  // };

  const handleEditClick = () => {
    const courtref = caseData?.viewDetails?.courtRef;
    let courtTypeForRoute = "";

    // Determine court type for route
    switch (selectedCourtType?.toLowerCase()) {
      case "high court":
      case "highcourt":
        courtTypeForRoute = "high";
        break;
      case "district court":
      case "districtcourt":
        courtTypeForRoute = "district";
        break;
      case "other court":
      case "othercourt":
        courtTypeForRoute = "other";
        break;
      case "supreme court":
      case "supremecourt":
        courtTypeForRoute = "supreme";
        break;
      default:
        courtTypeForRoute = "high"; // default fallback
        break;
    }

    if (courtref) {
      navigate(`/home/cases/editcase/${courtTypeForRoute}/${courtref}`, {
        state: { selectedCourtType, selectedCaseType },
      });
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleReAppealClick = () => {
    setShowReAppealModal(true);
  };

  const handleReAppealSubmit = async (reAppealSubmitData) => {
    try {
      const courtRefVar = caseData?.viewDetails?.courtRef;

      if (!courtRefVar) {
        throw new Error("Court reference not found");
      }

      const payload = {
        courtRef: courtRefVar,
        appealPeriodDays: reAppealSubmitData.appealPeriodDays,
        appealDeadlineDate: reAppealSubmitData.appealDeadlineDate,
      };

      // Call the API
      const response = await AddOrUpdateReAppealInCases(payload);

      // Handle the response - it might return the updated/created object
      let responseData = response;
      if (response?.data) {
        responseData = response.data;
      }

      // Create updated re-appeal data object with all necessary fields
      const updatedReAppealData = {
        _id: responseData?._id || reAppealData?._id || `temp_${Date.now()}`,
        courtRef: courtRefVar,
        appealPeriodDays: reAppealSubmitData.appealPeriodDays,
        appealDeadlineDate: reAppealSubmitData.appealDeadlineDate,
        companyId: responseData?.companyId || reAppealData?.companyId,
        createdBy: responseData?.createdBy || reAppealData?.createdBy,
        createdAt:
          responseData?.createdAt ||
          reAppealData?.createdAt ||
          new Date().toISOString(),
        updatedAt: responseData?.updatedAt || new Date().toISOString(),
        __v: responseData?.__v || reAppealData?.__v || 0,
      };

      // Update local state immediately
      setReAppealData(updatedReAppealData);

      showToast(
        reAppealData
          ? "Re-appeal data updated successfully!"
          : "Re-appeal data added successfully!",
        "success",
      );
    } catch (error) {
      console.error("Error saving re-appeal data:", error);
      showToast("Failed to save re-appeal data. Please try again.", "error");
      throw error;
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    const courtref = caseData?.viewDetails?.courtRef;

    try {
      // Call appropriate API based on selectedCourtType (removed unused deleteResponse variable)
      switch (selectedCourtType?.toLowerCase()) {
        case "high court":
        case "highcourt":
          await DeleteHighCourtManualCaseByCourtRef(courtref);
          break;
        case "district court":
        case "districtcourt":
          await DeleteDistrictCourtManualCaseByCourtRef(courtref);
          break;
        case "other court":
        case "othercourt":
          await DeleteOtherCourtManualCaseByCourtRef(courtref);
          break;
        case "supreme court":
        case "supremecourt":
          await DeleteSupremeCourtManualCaseByCourtRef(courtref);
          break;
        default:
          // Default to high court if type is unclear
          await DeleteHighCourtManualCaseByCourtRef(courtref);
          break;
      }

      // Show success toast - pass as separate parameters
      showToast("Case deleted successfully!", "success");

      // Refresh case limit data after deletion
      await refreshCaseLimitData();

      triggerCaseRefresh();

      // Call parent callback to remove case from list
      if (onCaseDeleted) {
        onCaseDeleted(courtref);
      }
      //navigate(0);
    } catch (error) {
      console.error("Error deleting case:", error);

      // Show error toast - pass as separate parameters
      showToast("Failed to delete case. Please try again.", "error");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const bubbleEntries = [
    ...(bubbleSettings?.case?.highCourt || []),
    ...(bubbleSettings?.case?.districtCourt || []),
  ];

  const bubbleMatch = bubbleEntries.find(
    (entry) => entry.courtRef === courtRef,
  );
  const { newCaseAssignment, caseAssignmentNotificationMap } = useMemo(() => {
    const entries = [
      ...(bubbleSettings?.case?.highCourt || []),
      ...(bubbleSettings?.case?.districtCourt || []),
    ];

    const newCaseAssignment = {};
    const caseAssignmentNotificationMap = {};

    // entries.forEach((entry) => {
    //   entry.assignment?.forEach((caseAssignmentEntry) => {
    //     if (["Case Assigned"].includes(caseAssignmentEntry.action)) {
    //       const caseAssignmentID = caseAssignmentEntry.source;
    //       newCaseAssignment[caseAssignmentID] = caseAssignmentEntry.action;
    //       caseAssignmentNotificationMap[caseAssignmentID] = caseAssignmentEntry.id;
    //     }
    //   });
    // });

    entries.forEach((entry) => {
      if (entry.courtRef === courtRef) {
        entry.assignment?.forEach((caseAssignmentEntry) => {
          if (["Case Assigned"].includes(caseAssignmentEntry.action)) {
            // Use courtRef as the key instead of source
            newCaseAssignment[courtRef] = caseAssignmentEntry.action;
            caseAssignmentNotificationMap[courtRef] = caseAssignmentEntry.id;
          }
        });
      }
    });

    return { newCaseAssignment, caseAssignmentNotificationMap };
  }, [bubbleSettings, courtRef]);

  
  const { caseAutoUpdateMap } = useMemo(() => {
    const entries = [
      ...(bubbleSettings?.case?.highCourt || []),
      ...(bubbleSettings?.case?.districtCourt || []),
    ];

    const caseAutoUpdateMap = {};

    entries.forEach((entry) => {
      if (entry.courtRef === courtRef) {
        // Initialize array for this Court Ref
        if (!caseAutoUpdateMap[courtRef]) {
          caseAutoUpdateMap[courtRef] = [];
        }

        // STRICTLY check only for 'Case Auto Updated' actions [cite: 6]
        // We intentionally DO NOT check entry.assignment, entry.task, or entry.document
        // as per your request to leave those notifications 'unseen'.
        entry.caseUpdated?.forEach((updateEntry) => {
          if (["Case Auto Updated"].includes(updateEntry.action)) {
            // Store the notification ID using courtRef as the key
            caseAutoUpdateMap[courtRef].push(updateEntry.id);
          }
        });
      }
    });

    return { caseAutoUpdateMap };
  }, [bubbleSettings, courtRef]);

  // const handleCaseAssignmentNotifyClick = async (caseAssignmentID) => {
  //   const notificationId = caseAssignmentNotificationMap[caseAssignmentID];

  //   if (notificationId) {
  //     try {
  //       await updateUnseenNotification(notificationId);
  //       await fetchAndUpdateBubbleSettings(); // Immediately refresh bubble UI
  //     } catch (err) {
  //       console.error("Failed to update notification:", err);
  //     }
  //   }
  // };

  const handleCaseAutoUpdateNotifyClick = async (currentCourtRef) => {
    // Retrieve only the 'Case Auto Updated' notification IDs
    const notificationIds = caseAutoUpdateMap[currentCourtRef];

    if (notificationIds && notificationIds.length > 0) {
      try {
        // Update status for these specific notifications only
        await Promise.all(
          notificationIds.map((id) => updateUnseenNotification(id)),
        );
        await fetchAndUpdateBubbleSettings();
      } catch (err) {
        console.error("Failed to update notification:", err);
      }
    }
  };

  const handlecasedetailByRef = () => {
    const courtref = caseData?.viewDetails?.courtRef;
    if (courtref) {
      // This will now ONLY clear 'Case Auto Updated' notifications
       handleCaseAutoUpdateNotifyClick(courtref);

      navigate(`/home/cases/casedetails/${courtref}`, {
        state: { selectedCourtType, selectedCaseType },
      });
    }
  };

  return (
    <>
      <div className="case-card" onClick={handlecasedetailByRef}>
        <div className="card-top">
          {showCheckboxes && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onSelectToggle}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          )}
          <div
            className={`serial-number ${showCheckboxes ? "serial-no-checkboxes" : ""}`}
          >
            {serialNumber} <span>)</span>
          </div>
          <div className={`card-header-content`}>
            <div className="parties-title">
              <span title={parties}>
                {/* {truncatePartyNames(parties)} */}
                <HighlightText
                  text={truncatePartyNames(parties)}
                  highlight={searchTerm}
                />
              </span>
              {bubbleMatch?.totalCount > 0 && (
                <span className="bubble-notification">
                  {bubbleMatch.totalCount}
                </span>
              )}
              {casecardlabels.length > 0 &&
                casecardlabels.map((label, index) => (
                  <span
                    key={index}
                    className="label-pill"
                    style={{ backgroundColor: label.color }}
                    title={label.name}
                  >
                    {label.name}
                  </span>
                ))}
            </div>

            <div className="files-status">
              <div className="filed-on">
                Filed on:
                {/* caseDetails["Filing Date"] || "N/A"} */}
                <HighlightText
                  text={caseDetails["Filing Date"] || "N/A"}
                  highlight={searchTerm}
                />
              </div>
              <div
                className={`status-pill ${caseStatusValue.toLowerCase().includes("disposed")
                  ? "disposed"
                  : "pending"
                  }`}
              >
                {caseStatusValue || "Pending"}
              </div>
            </div>
          </div>

          {/* Re-Appeal Button - Only show for disposed cases */}
          {isDisposed && isAppealable && role !== "customer" && (
            <button
              className={`reappeal-btn ${reAppealData ? "has-data" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                handleReAppealClick();
              }}
              title={
                reAppealData ? "Update Re-Appeal Data" : "Add Re-Appeal Data"
              }
            >
              <MdAdd />
            </button>
          )}
        </div>

        <div className="case-meta">
          <div className="case-meta-main">
            <div className="case-meta-details">
              <p className="case-card-detail">Case No :</p>
              <p>
                {/* {caseDetails["Filing Number"] || "N/A"} */}

                <HighlightText
                  text={caseDetails["Filing Number"] || "N/A"}
                  highlight={searchTerm}
                />
              </p>
            </div>
            <div className="case-meta-details">
              <p className="case-card-detail">CNR :</p>
              <p>
                {/* {caseDetails["CNR Number"] || "N/A"} */}
                <HighlightText
                  text={caseDetails["CNR Number"] || "N/A"}
                  highlight={searchTerm}
                />
              </p>
            </div>
            <div className="case-meta-details">
              <p className="case-card-detail">File No :</p>
              <p>
                {/* {caseDetails["Filing Number"] || "N/A"} */}
                <HighlightText
                  text={caseDetails["Filing Number"] || "N/A"}
                  highlight={searchTerm}
                />
              </p>
            </div>
          </div>
          {caseData?.assignedTo &&
            caseData.assignedTo.length > 0 &&
            role !== "customer" && (
              <div className="case-meta-main-assigned">
                {/* Assigned To Box - Display advocate names */}
                {caseData?.assignedTo && caseData.assignedTo.length > 0 && (
                  <div className="assigned-to-box">
                    <p className="assigned-to-label">
                      <strong>Assigned To:</strong>
                    </p>
                    <div className="assigned-advocates">
                      {caseData.assignedTo.map((advocate, index) => (
                        <span
                          key={advocate.id || index}
                          className="advocate-name"
                        >
                          {advocate.name}
                          {index < caseData.assignedTo.length - 1 && ", "}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          {/* 
          <div className="case-meta-mob-edit">
            <button className="edit-btn-assign mobile-edit">✎</button>
          </div> */}
        </div>

        <hr />
        <div className="court-details">
          {/* <div className='case-meta-court'>
            <p className='courtnames'>{highCourtMap[caseData['court']] || "Unknown Court"}</p>
            {caseData?.actsData && Array.isArray(caseData.actsData) && caseData.actsData.map((act, index) => (
              <div key={index}>
                {act?.underAct && <p>{act?.underAct}</p>}
                {act?.underSection && <p>{act?.underSection}</p>}
              </div>
            ))}
          </div> */}

          <div className="case-meta-court">
            <p className="courtnames">
              {/* {caseData?.courtName && caseData.courtName.trim() !== ""
                ? caseData.courtName : "Unknown Court"} */}
              <HighlightText
                text={
                  caseData?.courtName && caseData.courtName.trim() !== ""
                    ? caseData.courtName
                    : "Unknown Court"
                }
                highlight={searchTerm}
              />
            </p>

            {/* {caseData?.actsData &&
              Array.isArray(caseData.actsData) &&
              caseData.actsData.map((act, index) => (
                <div key={index}>
                  {act?.underAct && <p>
                    
                    <HighlightText
                      text={act?.underAct}
                      highlight={searchTerm}
                    />
                    </p>}
                  {act?.underSection && <p>
                    <HighlightText
                      text={act?.underSection}
                      highlight={searchTerm}
                    />
                    </p>}
                </div>
              ))} */}

            {[
              ...(caseData?.actsData || []),
              ...(caseData?.actsAndSections || []),
            ].map((item, index) => {
              // Determine the correct key to use based on which object type it is
              const actName = item.underAct || item.act;
              const sectionValue = item.underSection || item.section;

              // If neither exists, don't render empty divs
              if (!actName && !sectionValue) return null;

              return (
                <div key={index}>
                  {actName && (
                    <p>
                      <HighlightText text={actName} highlight={searchTerm} />
                    </p>
                  )}
                  {sectionValue && (
                    <p>
                      <HighlightText
                        text={sectionValue}
                        highlight={searchTerm}
                      />
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <div
            className={`bottom-bar`}
            style={{ marginBottom: expanded && !isMobile ? "20px" : "0" }}
          >
            {!isMobile && role !== "customer" && (
              <>
                {isDisposed && reAppealData && (
                  <div className="reappeal-info-bottom">
                    {reAppealData.appealPeriodDays != null && (
                      <div className="reappeal-item-bottom">
                        <p>
                          <strong>Appeal Period</strong>
                        </p>
                        <p>{reAppealData.appealPeriodDays} days</p>
                      </div>
                    )}
                    {reAppealData.appealDeadlineDate && (
                      <div className="reappeal-item-bottom">
                        <p>
                          <strong>Appeal Deadline</strong>
                        </p>
                        <p>{reAppealData.appealDeadlineDate}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="date-box">
                  {caseData?.caseStatus?.["Decision Date"] ? (
                    <>
                      <p>
                        <strong>Decision Date</strong>
                      </p>
                      <p>{caseData.caseStatus["Decision Date"]}</p>
                    </>
                  ) : (
                    <>
                      <strong>Next Hearing Date</strong>
                      <p
                        className={
                          caseData?.customUndatedRows?.["Next Hearing Date"]
                            ? "custom-date"
                            : ""
                        }
                      >
                        {/* {caseData && caseData.caseStatus && caseData.caseStatus["Next Hearing Date"] ? caseData.caseStatus["Next Hearing Date"] : "N/A"} */}
                        <HighlightText
                          text={
                            caseData?.customUndatedRows?.[
                              "Next Hearing Date"
                            ] ||
                            (caseData &&
                            caseData.caseStatus &&
                            caseData.caseStatus["Next Hearing Date"]
                              ? caseData.caseStatus["Next Hearing Date"]
                              : "N/A")
                          }
                          highlight={searchTerm}
                        />
                      </p>
                    </>
                  )}
                </div>

                <div
                  className={`assigned-box ${
                    selectedCaseType === "fetched"
                      ? "fetched-case-assigned"
                      : ""
                  }`}
                >
                  <p>
                    <strong>Advocates</strong>
                  </p>
                  <p>
                    {/* {WrittenFormat(caseData?.advocates || "Unassigned")} */}
                    <HighlightText
                      text={WrittenFormat(caseData?.advocates || "Unassigned")}
                      highlight={searchTerm}
                    />
                  </p>
                </div>
              </>
            )}
            {selectedCaseType === "added" && role !== "customer" && (
              <div className="actions-case-card">
                <button
                  className="edit-btn-assign"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick();
                  }}
                  title="Edit Case"
                >
                  <MdEdit />
                </button>
                <button
                  className="edit-btn-assign delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick();
                  }}
                  disabled={isDeleting}
                  title="Delete Case"
                >
                  <MdDelete />
                </button>
              </div>
            )}
          </div>
        </div>

        {!isMobile && (
          <div
            className="expand-toggle-bottom"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
          >
            <div className={`circle-arrow ${expanded ? "up" : "down"}`}>
              <IoIosArrowDown />
            </div>
          </div>
        )}

        {!isMobile && (
          <div
            className="card-details"
            ref={detailsRef}
            style={{ overflow: "hidden", display: "none" }}
          >
            <div className="card-det-div">
              <div className="card-section">
                <h5>Coram</h5>
                {caseData?.caseStatus?.["Coram"] ||
                caseData?.caseStatus?.["Court Number and Judge"] ? (
                  <div className="case-extend-details-card">
                    <p>
                      {/* {caseData.caseStatus["Coram"]} */}
                      <HighlightText
                        text={
                          caseData?.caseStatus?.["Coram"] ||
                          caseData?.caseStatus?.["Court Number and Judge"]
                        }
                        highlight={searchTerm}
                      />
                    </p>
                  </div>
                ) : (
                  <p>--</p>
                )}
              </div>

              {/* <div className="card-section">
                <h5>Documents</h5>
                {caseData?.documents?.length === 0 ? (
                  <p>--</p>
                ) : (
                  <div className="docs-grid">
                    {caseData?.documents?.map((doc, index) => (
                      <div key={index} className="case-extend-details-card">
                        <p>Doc No: {doc.documentNo}</p>
                        <p>Receiving Date:
                          <HighlightText
                            text={doc.dateOfReceiving}
                            highlight={searchTerm}
                          />
                        </p>
                        <p>Filed By:
                          <HighlightText
                            text={doc.filedBy}
                            highlight={searchTerm}
                          />
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div> */}
              {caseData?.documents && caseData.documents.length > 0 && (
                <div className="card-section">
                  <h5>Documents</h5>
                  <div className="docs-grid">
                    {caseData.documents.map((doc, index) => (
                      <div key={index} className="case-extend-details-card">
                        {doc.documentNo && <p>Doc No: {doc.documentNo}</p>}

                        {doc.dateOfReceiving && (
                          <p>
                            Receiving Date:{" "}
                            <HighlightText
                              text={doc.dateOfReceiving}
                              highlight={searchTerm}
                            />
                          </p>
                        )}

                        {doc.filedBy && (
                          <p>
                            Filed By:{" "}
                            <HighlightText
                              text={doc.filedBy}
                              highlight={searchTerm}
                            />
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* <div className="card-section">
                <h5>Orders</h5>
                {caseData?.orderInfo?.length === 0 ? (
                  <p>--</p>
                ) : (
                  caseData?.orderInfo?.map((ord, index) => (
                    <div key={index} className="case-extend-details-card">
                      <p>Order Date: 
                        <HighlightText
                          text={ord.orderDate}
                          highlight={searchTerm}
                        />
                      </p>
                      <p>Order On: 
                        <HighlightText
                          text={ord.orderOn}
                          highlight={searchTerm}
                        />
                      </p>
                      <p>Judge: 
                        <HighlightText
                          text={ord.judge}
                          highlight={searchTerm}
                        />
                      </p>
                    </div>
                  ))
                )}
              </div> */}
              <div className="card-section">
                <h5>Orders</h5>
                {!caseData?.orderInfo || caseData.orderInfo.length === 0 ? (
                  <p>--</p>
                ) : (
                  caseData.orderInfo.map((ord, index) => (
                    <div key={index} className="case-extend-details-card">
                      {ord.orderDate && (
                        <p>
                          Order Date:{" "}
                          <HighlightText
                            text={ord.orderDate}
                            highlight={searchTerm}
                          />
                        </p>
                      )}

                      {ord.orderOn && (
                        <p>
                          Order On:{" "}
                          <HighlightText
                            text={ord.orderOn}
                            highlight={searchTerm}
                          />
                        </p>
                      )}

                      {ord.judge && (
                        <p>
                          Judge:{" "}
                          <HighlightText
                            text={ord.judge}
                            highlight={searchTerm}
                          />
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* <div className="card-section">
                <h5>Case History Timeline</h5>
                {caseData?.historyCaseData?.length === 0 ? (
                  <p>--</p>
                ) : (
                  caseData?.historyCaseData?.map((item, index) => (
                    <div key={index} className="case-extend-details-card">
                      <p>Business Date:
                        <HighlightText
                          text={item.businessOnDate || '-'}
                          highlight={searchTerm}
                        />
                      </p>
                      <p>Hearing Date:
                        <HighlightText
                          text={item.hearingDate || '-'}
                          highlight={searchTerm}
                        />
                      </p>
                    </div>
                  ))
                )}
              </div> */}
              {/* Logic to determine which history array to use */}
              {(() => {
                const historyList =
                  caseData?.historyCaseData || caseData?.historyData;

                // If no list exists or list is empty, don't render anything
                if (!historyList || historyList.length === 0) return null;

                return (
                  <div className="card-section">
                    <h5>Case History Timeline</h5>
                    {historyList.map((item, index) => (
                      <div key={index} className="case-extend-details-card">
                        {item.businessOnDate && (
                          <p>
                            Business Date:{" "}
                            <HighlightText
                              text={item.businessOnDate}
                              highlight={searchTerm}
                            />
                          </p>
                        )}

                        {item.hearingDate && (
                          <p>
                            Hearing Date:{" "}
                            <HighlightText
                              text={item.hearingDate}
                              highlight={searchTerm}
                            />
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        itemType="this case"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Re-Appeal Modal - Only render for disposed cases */}
      {isDisposed && isAppealable && (
        <ReAppealModal
          isOpen={showReAppealModal}
          onClose={() => setShowReAppealModal(false)}
          onSubmit={handleReAppealSubmit}
          decisionDate={
            caseData?.caseStatus?.["Decision Date"] ||
            caseDetails["Filing Date"]
          }
          existingReAppealData={reAppealData}
        />
      )}
    </>
  );
}
